import { arrow, autoUpdate, flip, offset, Placement, shift, useFloating } from '@floating-ui/react-dom';
import { FloatingPortal } from '@floating-ui/react-dom-interactions';
import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { Colors, remInPx } from '../../../ui';
import { PopArrow } from '../../../ui/PopArrow';
import { TipFrame } from '../../../ui/Tip';
import { useResolve } from '../../Container';
import { Copy, Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { PopupManager } from '../../popups/PopupManager';
import { HelpController } from './HelpController';

export interface HelpTip {
    readonly key: string;
    readonly mount: string;
    readonly title: string;
    readonly message: string;
}

export const HelpTip = (key: string, mount: string, title: string, message: string) =>
    ({ key, mount, title, message }) as HelpTip;

const TipReference = styled.div`
    position: relative;
`;

const fadeIn = keyframes`
    from { opacity: 0 }
    to { opacity: 1 }
`;

const TipHighlight = styled.div`
    position: absolute;
    inset: 0;
    border: solid .125rem ${Colors.mystery};
    border-radius: .375rem;
    opacity: 0;
    animation: .15s ${fadeIn} .3s ease-out;
    animation-fill-mode: forwards;
    pointer-events: none;
`;

const TipFrameStyle = styled.div`

    ${TipFrame} {
        text-align: left;
        background: ${Colors.mystery};
        color: white;
        cursor: pointer;

        ${Copy}, ${Title} {
            color: white;
        }
    }
`;

const tipArrow = (position: Placement, left: any, top: any) => {
    return ({
        position,
        style: { left, top }
    });
};

export type HelpTipProps = {
    name: string,
    placement: Placement,
    highlight?: boolean,
};

export const HelpTipMount: LFC<HelpTipProps> = ({ name, placement, highlight, children }) => {

    const ctrl = useResolve(HelpController);
    const popups = useResolve(PopupManager);

    const noPopup = !popups.current;
    const { activeTips, libraryOpen } = useObserver(ctrl);
    const tip = activeTips[0];
    const open = noPopup && !libraryOpen && tip?.mount === name;

    const arrowRef = useRef<HTMLDivElement>();

    const {
        x,
        y,
        placement: correctedPlacement,
        reference,
        floating,
        strategy,
        refs,
        update,
        middlewareData: { arrow: { x: arrowX = 0, y: arrowY = 0 } = {} }
    } = useFloating({
        placement,
        middleware: [offset(remInPx(.5)), flip(), shift({ padding: remInPx(.5) }), arrow({ element: arrowRef })]
    });

    useEffect(() => {
        if (refs.reference.current && refs.floating.current && open) {
            return autoUpdate(refs.reference.current, refs.floating.current, update);
        }
    }, [refs.reference, refs.floating, update, open]);

    return <>
        <TipReference ref={reference}>
            {children}
            {highlight && open && <TipHighlight />}
        </TipReference>
        {open &&
            <FloatingPortal>
                <TipFrameStyle onClick={() => ctrl.dismissTip(activeTips[0]?.key)}>
                    <TipFrame ref={floating}
                        style={{
                            position: strategy,
                            top: y ?? '',
                            left: x ?? '',
                        }}
                    >
                        <Flexer flex="column" padding="1" maxWidth="24" gap=".5">
                            {tip && <Title size="xl" flush>{tip.title}</Title>}
                            {tip && <Copy flush>{tip.message}</Copy>}
                        </Flexer>
                        <PopArrow ref={arrowRef} {...tipArrow(correctedPlacement, arrowX, arrowY)} color={Colors.mystery} />
                    </TipFrame>
                </TipFrameStyle>
            </FloatingPortal>}
    </>;
};
